import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { MaterialIcon } from '@/ui/material-icon'
import { Button } from '@/ui/button'
import { Input } from '@/ui/input'
import { Label } from '@/ui/label'
import { Textarea } from '@/ui/textarea'
import { Separator } from '@/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu'
import { Badge } from '@/ui/badge'
import styles from './ConfigureTransformationSheet.module.scss'
import {
  useTransformations,
  buildInitialFormState,
} from '@/features/adp/hooks/UseTransformations'
import type { DynamicField, DynamicFieldOption, TransformTypeDefinition } from '@/features/adp/api/transformations.api'
import {
  applySessionOptionsToFields,
  getExtendedColumnOptionsForPipeline,
  getSessionColumnOptionsForTableSelection,
  getSessionTableOptions,
  integrationFieldLooksLikeTable,
  mergeSavedForm,
  readAdpPipelineSession,
  upsertTransformationSession,
  useAdpPipelineSessionSnapshot,
} from '@/features/adp/utils/adpPipelineSessionStorage'
// ─── Types ──────────────────────────────────────────────────────── 
export type ConfigureTransformationSheetProps = {
  onClose: () => void
  onBackToChoose?: () => void
  transformationTitle?: string
  connectorImage?: string
  configuringNodeId?: string | null
  connectorId?: string
}
type AppliedTransformation = {
  id: string
  label: string
  values: Record<string, unknown>
}
type FormState = Record<string, unknown>
type SavedAppliedTransformation = {
  id: string
  label: string
  values?: Record<string, unknown>
}
type RenameColumnsOperationPayload = {
  RC01: {
    SourceTable: string
    renameColumnsList: [string, string][]
  }
}
type FindReplaceOperationRow = {
  TransformationNo: 'FR01'
  SourceTable: string
  ReplaceInColumn: string
  ReplaceWhat: string
  ReplaceWith: string
}
type FindReplaceOperationPayload = {
  FR01: string
}
type DataTypeConversionOperationPayload = {
  DC01: {
    SourceTable: string
    dataTypeConversion_zip: [string, string][]
  }
}
type ConcatOperationRow = {
  TransformationNo: 'CC01'
  SourceTable: string
  New_ColumnName: string
  FirstColumn: string
  SecondColumn: string
  ConcatWith: string
}
type ConcatOperationPayload = {
  CC01: string
}
type DropFieldsOperationObject = {
  SourceTable: string
  dropFieldsList: string[]
}
type DropFieldsOperationPayload = {
  DF01: string
}
type SortOperationPayload = {
  SR01: {
    SourceTable: string
    sort_columns: string[]
    ascending: 'true' | 'false'
  }
}
type CaseSetOperationPayload = {
  CS01: {
    SourceTable: string
    case_change_columns: string[]
    case: string
  }
}
type FilterOperationRow = {
  TransformationNo: 'FL01'
  SourceTable: string
  SqlWhereClause: string
}
type FilterOperationPayload = {
  FL01: string
}
type DropRowsOperationRow = {
  TransformationNo: 'DR01'
  SourceTable: string
  SqlWhereClause: string
}
type DropRowsOperationPayload = {
  DR01: string
}
type DropDuplicatesOperationPayload = {
  DP01: {
    SourceTable: string
    entireRow: string
    onColumns: string
  }
}
type CumulativeOperationRow = {
  TransformationNo: 'CU01'
  SourceTable: string
  CumulativeType: string
  CumulateOnColumn: string
  NewColumnName: string
  GroupBy: string
  OrderBy: string
}
type CumulativeOperationPayload = {
  CU01: string
}
type DerivedColumnOperationRow = {
  TransformationNo: 'DE01'
  SourceTable: string
  DerivedColumnCondition: string
  WhereCondition: string
}
type DerivedColumnOperationPayload = {
  DE01: string
}
type TransposeHeader = {
  name: string
  dataType: string
}
type TransposeOperationObject = {
  SourceTable: string
  fixed: string[]
  ignore: string[]
  transpose: string[]
  transposeHeaders: TransposeHeader[]
}
type TransposeOperationPayload = {
  TP01: string
}
type BoxcoxOperationRow = {
  SourceTable: string
  column_name: string
  new_column_name: string
  lamda_value: string
}
type BoxcoxOperationPayload = {
  BC01: string
}
type ReplaceNullOperationRow = {
  TransformationNo: 'RN01'
  SourceTable: string
  column_name: string
  replacenullwith: string
  method: null
}
type ReplaceNullOperationPayload = {
  RN01: string
}
type DateToIntegerOperationRow = {
  TransformationNo: 'DI01'
  SourceTable: string
  DateColumn: string
  NewDateKeyColumnName: string
  KeyType: string
}
type DateToIntegerOperationPayload = {
  DI01: string
}
type ZscoreOperationRow = {
  SourceTable: string
  zscore_on_column: string
  new_column: string
}
type ZscoreOperationPayload = {
  ZS01: string
}
type SplitOperationRow = {
  TransformationNo: 'SP01'
  SourceTable: string
  Column: string
  Separator: string
  NewColumns: string
}
type SplitOperationPayload = {
  SP01: string
}
type ForwardFillOperationRow = {
  TransformationNo: 'FF01'
  SourceTable: string
  ForwardFillOnColumn: string
  GroupBy: string
  OrderBy: string
}
type ForwardFillOperationPayload = {
  FF01: string
}
type DateSplitOperationPayload = {
  DS01: string
}
type ReplaceMappingOperationPayload = {
  RM01: string
}
type AggregationOperationPayload = {
  AG01: {
    SourceTable: string
    aggregateList: [string, string[] | null, string, string, null][]
  }
}
type LookupOperationRow = {
  TransformationNo: 'LK01'
  SourceTable: string
  SourceColumn: string
  LookupTableName: string
  LookupTable_DataSource: string
  LookupTableName_isMaster: string
  LookUpColumns: string
  LookUpGetColumns: string
}
type LookupOperationPayload = {
  LK01: string
}
// ─── Constants ──────────────────────────────────────────────────── 
const FALLBACK_SCHEMA_OPTIONS = [
  'customer_id',
  'first_name',
  'last_name',
  'email',
  'user_id',
  'order_id',
]
const TRANSFORM_DATA_TYPE_OPTIONS: DynamicFieldOption[] = [
  { value: 'string', label: 'string' },
  { value: 'int', label: 'int' },
  { value: 'float', label: 'float' },
  { value: 'boolean', label: 'boolean' },
  { value: 'timestamp', label: 'timestamp' },
  { value: 'date', label: 'date' },
  { value: 'bigint', label: 'bigint' },
]
const CUMULATIVE_TYPE_OPTIONS: DynamicFieldOption[] = [
  { value: 'sum', label: 'Sum' },
  { value: 'count', label: 'Count' },
]

function transformationFieldLooksLikeCumulativeType(field: DynamicField): boolean {
  const probe = `${field.key} ${field.label}`.trim().toLowerCase()
  return /\b(cumulative\s*type|aggregate\s*type|agg\s*type)\b/.test(probe)
}

function transformationFieldLooksLikeDataType(field: DynamicField): boolean {
  if (transformationFieldLooksLikeCumulativeType(field)) return false
  const probe = `${field.key} ${field.label}`.trim().toLowerCase()
  // More specific pattern to avoid matching fields like "Sorting Type"
  return /\b(data\s*type|datatype|convert\s*to|target\s*type|new.*column.*data.*type)\b/.test(probe) &&
    !/\b(sorting\s*type|join\s*type|cumulative\s*type|aggregate\s*type|key\s*type)\b/.test(probe)
}
function applyDataTypeOptionsToFields(fields: DynamicField[]): DynamicField[] {
  return fields.map(field => {
    if (field.type === 'group' && field.fields?.length) {
      return {
        ...field,
        fields: applyDataTypeOptionsToFields(field.fields),
      }
    }
    if (field.type === 'select' || field.type === 'multiselect') {
      // Skip fields that already have API-defined options
      if (field.options?.length) {
        return field
      }

      if (transformationFieldLooksLikeCumulativeType(field)) {
        return {
          ...field,
          options: CUMULATIVE_TYPE_OPTIONS,
        }
      }
      if (transformationFieldLooksLikeDataType(field)) {
        return {
          ...field,
          options: TRANSFORM_DATA_TYPE_OPTIONS,
        }
      }
    }
    return field
  })
}
function isMetaFieldKey(key: string): boolean {
  return key.startsWith('__')
}
function toPersistableForm(form: FormState): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  Object.entries(form).forEach(([key, value]) => {
    if (isMetaFieldKey(key)) return
    out[key] = value
  })
  return out
}
function isRenameColumnsTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'edit') return true
  return (transformName ?? '').trim().toLowerCase() === 'rename columns'
}
function isFindReplaceTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'findreplace') return true
  return (transformName ?? '').trim().toLowerCase() === 'find and replace'
}
function isDataTypeConversionTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalizedConnector = connectorId.trim().toLowerCase()
  if (normalizedConnector === 'datetime') return true
  const normalizedName = (transformName ?? '').trim().toLowerCase()
  return normalizedName === 'date to integer' || normalizedName === 'data type conversion'
}
function isConcatTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'pivot') return true
  return (transformName ?? '').trim().toLowerCase() === 'concat'
}
function isDropFieldsTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'drop fields' || normalized === 'drop field'
}
function isSortTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'sort' || normalized === 'sort fields' || normalized === 'sort columns'
}
function isCaseSetTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'case set' || normalized === 'case change'
}
function isFilterTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'filter' || normalized === 'where filter'
}
function isDropRowsTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'drop rows' || normalized === 'drop row'
}
function isDropDuplicatesTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'drop duplicates' || normalized === 'drop duplicate'
}
function isCumulativeTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'datetime') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'cumulative' || normalized === 'cumulative sum'
}
function isDerivedColumnTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'edit') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'derived column' || normalized === 'derive column'
}
function isTransposeTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'pivot') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'transpose'
}
function isBoxcoxTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'datetime') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'boxcox' || normalized === 'box-cox' || normalized === 'box cox'
}
function isReplaceNullTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'edit') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'replace null' || normalized === 'replace nulls'
}
function isDateToIntegerTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'datetime') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'date to integer'
}
function isZscoreTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'datetime') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'zscore' || normalized === 'z-score'
}
function isSplitTransform(connectorId: string, transformName: string | undefined, transformId?: number): boolean {
  if (connectorId.trim().toLowerCase() === 'split') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  // Exclude date split specifically by name and ID
  if (normalized === 'date split' || transformId === 29) return false
  return normalized === 'split'
}
function isForwardFillTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'datetime') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'forward fill' || normalized === 'ffill'
}
function isDateSplitTransform(connectorId: string, transformName: string | undefined, transformId?: number): boolean {
  if (connectorId.trim().toLowerCase() === 'datetime') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  // Match by both name and ID for precision
  return normalized === 'date split' || transformId === 29
}
function isReplaceMappingTransform(connectorId: string, transformName: string | undefined): boolean {
  if (connectorId.trim().toLowerCase() === 'findreplace') return true
  const normalized = (transformName ?? '').trim().toLowerCase()
  return normalized === 'replace mapping'
}
function isAggregationTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalizedConnector = connectorId.trim().toLowerCase()
  if (normalizedConnector.includes('aggr')) return true
  const normalizedName = (transformName ?? '').trim().toLowerCase()
  return normalizedName.includes('aggregation') || normalizedName.includes('aggreagation')
}
function isLookupTransform(connectorId: string, transformName: string | undefined): boolean {
  const normalizedConnector = connectorId.trim().toLowerCase()
  if (normalizedConnector.includes('lookup')) return true
  const normalizedName = (transformName ?? '').trim().toLowerCase()
  return normalizedName.includes('lookup')
}
function pickSourceTable(values: Record<string, unknown>): string {
  const tableEntry = Object.entries(values).find(([key, value]) => {
    if (typeof value !== 'string' || !value.trim()) return false
    return /table|source_file|file_name/i.test(key)
  })
  return typeof tableEntry?.[1] === 'string' ? tableEntry[1].trim() : ''
}
function toRenamePair(obj: Record<string, unknown>): [string, string] | null {
  const oldName =
    (typeof obj.oldColumnName === 'string' && obj.oldColumnName) ||
    (typeof obj.sourceColumn === 'string' && obj.sourceColumn) ||
    (typeof obj.currentColumn === 'string' && obj.currentColumn) ||
    (typeof obj.from === 'string' && obj.from) ||
    (typeof obj.oldName === 'string' && obj.oldName) ||
    (typeof obj.columnName === 'string' && obj.columnName) ||
    ''
  const newName =
    (typeof obj.newColumnName === 'string' && obj.newColumnName) ||
    (typeof obj.targetColumn === 'string' && obj.targetColumn) ||
    (typeof obj.renamedColumn === 'string' && obj.renamedColumn) ||
    (typeof obj.to === 'string' && obj.to) ||
    (typeof obj.newName === 'string' && obj.newName) ||
    (typeof obj.renameTo === 'string' && obj.renameTo) ||
    ''
  if (!oldName.trim() || !newName.trim()) return null
  return [oldName.trim(), newName.trim()]
}
function pickRenamePairs(values: Record<string, unknown>): [string, string][] {
  const pairs: [string, string][] = []
  Object.values(values).forEach(value => {
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (Array.isArray(item) && item.length >= 2) {
          const from = String(item[0] ?? '').trim()
          const to = String(item[1] ?? '').trim()
          if (from && to) pairs.push([from, to])
          return
        }
        if (item && typeof item === 'object') {
          const pair = toRenamePair(item as Record<string, unknown>)
          if (pair) pairs.push(pair)
        }
      })
      return
    }
    if (value && typeof value === 'object') {
      const pair = toRenamePair(value as Record<string, unknown>)
      if (pair) pairs.push(pair)
    }
  })
  if (pairs.length > 0) return pairs
  const isRenameSourceColumnKey = (key: string): boolean => {
    const normalized = key.trim().toLowerCase()
    if (!normalized) return false
    if (/table|source[_\s-]?file|file[_\s-]?name/.test(normalized)) return false
    return /(old.*column|source.*column|current.*column|column.*name|^column$|^column[_\s-]|[_\s-]column$)/.test(normalized)
  }
  const singleOld = Object.entries(values).find(([key, value]) =>
    typeof value === 'string' && isRenameSourceColumnKey(key)
  )?.[1]
  const singleNew = Object.entries(values).find(([key, value]) =>
    typeof value === 'string' && /new|target|rename.*to/i.test(key)
  )?.[1]
  if (typeof singleOld === 'string' && typeof singleNew === 'string') {
    const from = singleOld.trim()
    const to = singleNew.trim()
    if (from && to) return [[from, to]]
  }
  return []
}
function pickStringField(
  values: Record<string, unknown>,
  keyPattern: RegExp
): string {
  const entry = Object.entries(values).find(([key, value]) => {
    if (typeof value !== 'string') return false
    if (!value.trim()) return false
    return keyPattern.test(key)
  })
  return typeof entry?.[1] === 'string' ? entry[1].trim() : ''
}
function buildRenameColumnsOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): RenameColumnsOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  const renameColumnsList = applied.flatMap(item => pickRenamePairs(item.values))
  const fallbackPairs = renameColumnsList.length > 0 ? renameColumnsList : pickRenamePairs(form)
  if (!sourceTable || fallbackPairs.length === 0) return null
  return {
    RC01: {
      SourceTable: sourceTable,
      renameColumnsList: fallbackPairs,
    },
  }
}
function toFindReplaceRow(values: Record<string, unknown>, sourceTable: string): FindReplaceOperationRow | null {
  const replaceInColumn = pickStringField(values, /replace.*(in|column)|column/i)
  const replaceWhat = pickStringField(values, /replace.*what|find|search|old.*value/i)
  const replaceWith = pickStringField(values, /replace.*with|new.*value|to/i)
  if (!sourceTable || !replaceInColumn || !replaceWhat || !replaceWith) return null
  return {
    TransformationNo: 'FR01',
    SourceTable: sourceTable,
    ReplaceInColumn: replaceInColumn,
    ReplaceWhat: replaceWhat,
    ReplaceWith: replaceWith,
  }
}
function buildFindReplaceOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): FindReplaceOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toFindReplaceRow(item.values, sourceTable))
    .filter((row): row is FindReplaceOperationRow => row !== null)
  const fallbackRow = toFindReplaceRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    FR01: JSON.stringify(finalRows),
  }
}
function toDataTypeConversionPair(obj: Record<string, unknown>): [string, string] | null {
  const sourceColumn =
    (typeof obj.sourceColumn === 'string' && obj.sourceColumn) ||
    (typeof obj.columnName === 'string' && obj.columnName) ||
    (typeof obj.column === 'string' && obj.column) ||
    (typeof obj.fieldName === 'string' && obj.fieldName) ||
    (typeof obj.from === 'string' && obj.from) ||
    ''
  const targetDataType =
    (typeof obj.targetDataType === 'string' && obj.targetDataType) ||
    (typeof obj.dataType === 'string' && obj.dataType) ||
    (typeof obj.convertTo === 'string' && obj.convertTo) ||
    (typeof obj.to === 'string' && obj.to) ||
    ''
  if (!sourceColumn.trim() || !targetDataType.trim()) return null
  return [sourceColumn.trim(), targetDataType.trim()]
}
function pickDataTypeConversionPairs(values: Record<string, unknown>): [string, string][] {
  const pairs: [string, string][] = []
  Object.values(values).forEach(value => {
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (Array.isArray(item) && item.length >= 2) {
          const from = String(item[0] ?? '').trim()
          const to = String(item[1] ?? '').trim()
          if (from && to) pairs.push([from, to])
          return
        }
        if (item && typeof item === 'object') {
          const pair = toDataTypeConversionPair(item as Record<string, unknown>)
          if (pair) pairs.push(pair)
        }
      })
      return
    }
    if (value && typeof value === 'object') {
      const pair = toDataTypeConversionPair(value as Record<string, unknown>)
      if (pair) pairs.push(pair)
    }
  })
  if (pairs.length > 0) return pairs
  // Important: avoid picking the source *table* name as the “column”.
  // Many form keys contain `source` but are not the target column selector.
  const singleColumn = (() => {
    const isColumnKey = (key: string) => {
      const k = key.trim().toLowerCase()
      if (!k) return false
      // Exclude keys that are clearly about tables/schemas/files.
      if (/(table|schema|file|dataset|source[_\s-]?table|source[_\s-]?file)/.test(k)) return false
      // Keep keys that indicate a column/field selection.
      return /(column|field)(.*name)?/.test(k) || /source.*column/.test(k)
    }

    for (const [key, value] of Object.entries(values)) {
      if (!isColumnKey(key)) continue
      if (typeof value !== 'string') continue
      const v = value.trim()
      if (!v) continue
      return v
    }
    return ''
  })()
  const singleDataType = pickStringField(values, /data.*type|convert.*to|target.*type|to/i)
  if (singleColumn && singleDataType) return [[singleColumn, singleDataType]]
  return []
}
function buildDataTypeConversionOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): DataTypeConversionOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  const pairs = applied.flatMap(item => pickDataTypeConversionPairs(item.values))
  const fallbackPairs = pairs.length > 0 ? pairs : pickDataTypeConversionPairs(form)
  if (!sourceTable || fallbackPairs.length === 0) return null
  return {
    DC01: {
      SourceTable: sourceTable,
      dataTypeConversion_zip: fallbackPairs,
    },
  }
}
function toConcatRow(values: Record<string, unknown>, sourceTable: string): ConcatOperationRow | null {
  const newColumnName = pickStringField(values, /new.*column|output.*column|column.*name/i)
  const firstColumn = pickStringField(values, /first.*column|left.*column|column1|source.*column/i)
  const secondColumn = pickStringField(values, /second.*column|right.*column|column2|target.*column/i)
  const concatWith =
    pickStringField(values, /concat.*with|separator|delimiter|join.*with/i) || ' '
  if (!sourceTable || !newColumnName || !firstColumn || !secondColumn) return null
  return {
    TransformationNo: 'CC01',
    SourceTable: sourceTable,
    New_ColumnName: newColumnName,
    FirstColumn: firstColumn,
    SecondColumn: secondColumn,
    ConcatWith: concatWith,
  }
}
function buildConcatOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): ConcatOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toConcatRow(item.values, sourceTable))
    .filter((row): row is ConcatOperationRow => row !== null)
  const fallbackRow = toConcatRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    CC01: JSON.stringify(finalRows),
  }
}
function pickDropFields(values: Record<string, unknown>): string[] {
  const fields = new Set<string>()
  
  // Look for specific field keys that contain the columns to drop
  Object.entries(values).forEach(([key, value]) => {
    const normalizedKey = key.trim().toLowerCase()
    
    // Skip source table and other non-column fields
    if (/source.*table|table.*name|source.*file/i.test(normalizedKey)) return
    
    // Look for keys that specifically indicate columns/fields to drop
    if (!/drop.*field|drop.*column|field.*to.*drop|column.*to.*drop|fields?$|columns?$/i.test(normalizedKey)) return
    
    if (typeof value === 'string') {
      const v = value.trim()
      if (v) fields.add(v)
      return
    }
    if (Array.isArray(value)) {
      value.forEach(item => {
        const v = String(item ?? '').trim()
        if (v) fields.add(v)
      })
      return
    }
    if (value && typeof value === 'object') {
      Object.values(value as Record<string, unknown>).forEach(inner => {
        const v = String(inner ?? '').trim()
        if (v) fields.add(v)
      })
    }
  })
  
  return [...fields]
}
function buildDropFieldsOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): DropFieldsOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  const dropped = applied.flatMap(item => pickDropFields(item.values))
  const dropFieldsList = dropped.length > 0 ? dropped : pickDropFields(form)
  if (!sourceTable || dropFieldsList.length === 0) return null
  const payload: DropFieldsOperationObject = {
    SourceTable: sourceTable,
    dropFieldsList,
  }
  return {
    DF01: JSON.stringify(payload),
  }
}
function pickSortColumns(values: Record<string, unknown>): string[] {
  const out = new Set<string>()
  Object.entries(values).forEach(([key, value]) => {
    if (!/sort|column|field/i.test(key)) return
    if (typeof value === 'string') {
      const v = value.trim()
      if (v) out.add(v)
      return
    }
    if (Array.isArray(value)) {
      value.forEach(item => {
        const v = String(item ?? '').trim()
        if (v) out.add(v)
      })
    }
  })
  return [...out]
}
function pickAscending(values: Record<string, unknown>): 'true' | 'false' {
  const direct = Object.entries(values).find(([key]) => /asc|ascending|order|sort/i.test(key))?.[1]
  if (typeof direct === 'boolean') return direct ? 'true' : 'false'
  if (typeof direct === 'string') {
    const v = direct.trim().toLowerCase()
    if (v === 'true' || v === 'asc' || v === 'ascending') return 'true'
    if (v === 'false' || v === 'desc' || v === 'descending') return 'false'
  }
  return 'true'
}
function buildSortOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): SortOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  const sortColumnsFromApplied = applied.flatMap(item => pickSortColumns(item.values))
  const sortColumns = sortColumnsFromApplied.length > 0 ? sortColumnsFromApplied : pickSortColumns(form)
  if (!sourceTable || sortColumns.length === 0) return null
  return {
    SR01: {
      SourceTable: sourceTable,
      sort_columns: sortColumns,
      ascending: pickAscending(form),
    },
  }
}
function pickCaseChangeColumns(values: Record<string, unknown>): string[] {
  const out = new Set<string>()
  Object.entries(values).forEach(([key, value]) => {
    if (!/case|capitalize|upper|lower|title/i.test(key)) return
    if (typeof value === 'string') {
      const v = value.trim()
      if (v) out.add(v)
      return
    }
    if (Array.isArray(value)) {
      value.forEach(item => {
        const v = String(item ?? '').trim()
        if (v) out.add(v)
      })
    }
  })
  return [...out]
}
function pickCaseMode(values: Record<string, unknown>): string {
  const mode = pickStringField(values, /case|capitalize|upper|lower|title/)
  return mode || 'capitalize'
}
function buildCaseSetOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): CaseSetOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  const columnsFromApplied = applied.flatMap(item => pickCaseChangeColumns(item.values))
  const caseChangeColumns =
    columnsFromApplied.length > 0 ? columnsFromApplied : pickCaseChangeColumns(form)
  const caseMode =
    applied.map(item => pickCaseMode(item.values)).find(Boolean) || pickCaseMode(form)
  if (!sourceTable || caseChangeColumns.length === 0) return null
  return {
    CS01: {
      SourceTable: sourceTable,
      case_change_columns: caseChangeColumns,
      case: caseMode,
    },
  }
}
function toFilterRow(values: Record<string, unknown>, sourceTable: string): FilterOperationRow | null {
  const sqlWhereClause = pickStringField(values, /where|sql|clause|condition|filter/i)
  if (!sourceTable || !sqlWhereClause) return null
  return {
    TransformationNo: 'FL01',
    SourceTable: sourceTable,
    SqlWhereClause: sqlWhereClause,
  }
}
function buildFilterOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): FilterOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toFilterRow(item.values, sourceTable))
    .filter((row): row is FilterOperationRow => row !== null)
  const fallbackRow = toFilterRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    FL01: JSON.stringify(finalRows),
  }
}
function toDropRowsRow(values: Record<string, unknown>, sourceTable: string): DropRowsOperationRow | null {
  const sqlWhereClause = pickStringField(values, /where|sql|clause|condition|drop.*row|filter/i)
  if (!sourceTable || !sqlWhereClause) return null
  return {
    TransformationNo: 'DR01',
    SourceTable: sourceTable,
    SqlWhereClause: sqlWhereClause,
  }
}
function buildDropRowsOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): DropRowsOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toDropRowsRow(item.values, sourceTable))
    .filter((row): row is DropRowsOperationRow => row !== null)
  const fallbackRow = toDropRowsRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    DR01: JSON.stringify(finalRows),
  }
}
function pickEntireRowMode(values: Record<string, unknown>): string {
  const v = pickStringField(values, /entire.*row|all.*columns|full.*row|row/i)
  if (!v) return 'Yes'
  const normalized = v.trim().toLowerCase()
  if (normalized === 'yes' || normalized === 'true' || normalized === 'y') return 'Yes'
  if (normalized === 'no' || normalized === 'false' || normalized === 'n') return 'No'
  return v
}
function pickOnColumns(values: Record<string, unknown>): string {
  const direct = pickStringField(values, /on.*columns|columns|column|fields|keys/i)
  if (direct) return direct
  const arrEntry = Object.entries(values).find(([key, value]) =>
    /on.*columns|columns|fields|keys/i.test(key) && Array.isArray(value)
  )?.[1]
  if (Array.isArray(arrEntry)) {
    return arrEntry.map(item => String(item ?? '').trim()).filter(Boolean).join(',')
  }
  return ''
}
function buildDropDuplicatesOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): DropDuplicatesOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const firstApplied = applied[0]?.values
  const values = firstApplied ?? form
  const onColumns = pickOnColumns(values)
  const entireRow = pickEntireRowMode(values)
  if (!onColumns && entireRow !== 'Yes') return null
  return {
    DP01: {
      SourceTable: sourceTable,
      entireRow,
      onColumns,
    },
  }
}
function toCumulativeRow(
  values: Record<string, unknown>,
  sourceTable: string
): CumulativeOperationRow | null {
  const cumulativeType = pickStringField(values, /cumulative.*type|agg|aggregate|sum|avg|min|max|count/i)
  const cumulateOnColumn = pickStringField(values, /cumulate.*column|on.*column|target.*column|column/i)
  const newColumnName = pickStringField(values, /new.*column|output.*column|result.*column/i)
  const groupBy = pickStringField(values, /group.*by|partition.*by/i)
  const orderBy = pickStringField(values, /order.*by|sort.*by/i)
  if (!sourceTable || !cumulativeType || !cumulateOnColumn || !newColumnName) return null
  return {
    TransformationNo: 'CU01',
    SourceTable: sourceTable,
    CumulativeType: cumulativeType,
    CumulateOnColumn: cumulateOnColumn,
    NewColumnName: newColumnName,
    GroupBy: groupBy,
    OrderBy: orderBy,
  }
}
function buildCumulativeOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): CumulativeOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toCumulativeRow(item.values, sourceTable))
    .filter((row): row is CumulativeOperationRow => row !== null)
  const fallbackRow = toCumulativeRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    CU01: JSON.stringify(finalRows),
  }
}
function toDerivedColumnRow(
  values: Record<string, unknown>,
  sourceTable: string
): DerivedColumnOperationRow | null {
  const derivedColumnCondition = pickStringField(values, /derived.*condition|derived|expression|case.*when/i)
  const whereCondition = pickStringField(values, /where.*condition|where|filter.*condition/i)
  if (!sourceTable || !derivedColumnCondition) return null
  return {
    TransformationNo: 'DE01',
    SourceTable: sourceTable,
    DerivedColumnCondition: derivedColumnCondition,
    WhereCondition: whereCondition,
  }
}
function buildDerivedColumnOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): DerivedColumnOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toDerivedColumnRow(item.values, sourceTable))
    .filter((row): row is DerivedColumnOperationRow => row !== null)
  const fallbackRow = toDerivedColumnRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    DE01: JSON.stringify(finalRows),
  }
}
function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value.map(item => String(item ?? '').trim()).filter(Boolean)
}
function pickStringArrayByKey(values: Record<string, unknown>, pattern: RegExp): string[] {
  const direct = Object.entries(values).find(([key, value]) => pattern.test(key) && Array.isArray(value))?.[1]
  const arr = toStringArray(direct)
  if (arr.length > 0) return arr
  const set = new Set<string>()
  Object.entries(values).forEach(([key, value]) => {
    if (!pattern.test(key)) return
    if (typeof value === 'string') {
      const v = value.trim()
      if (v) set.add(v)
      return
    }
    toStringArray(value).forEach(v => set.add(v))
  })
  return [...set]
}
function toTransposeHeaders(values: Record<string, unknown>): TransposeHeader[] {
  const headersKey = Object.entries(values).find(([key]) => /transpose.*headers|headers/i.test(key))?.[1]
  if (Array.isArray(headersKey)) {
    const mapped = headersKey
      .map(item => {
        if (!item || typeof item !== 'object') return null
        const obj = item as Record<string, unknown>
        const name = String(obj.name ?? obj.columnName ?? '').trim()
        const dataType = String(obj.dataType ?? obj.type ?? '').trim()
        if (!name || !dataType) return null
        return { name, dataType }
      })
      .filter((v): v is TransposeHeader => v !== null)
    if (mapped.length > 0) return mapped
  }
  const headerName = pickStringField(values, /header.*name|new.*column|name/i)
  const headerType = pickStringField(values, /header.*type|data.*type|type/i)
  if (headerName && headerType) {
    return [{ name: headerName, dataType: headerType }]
  }
  return []
}
function buildTransposeOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): TransposeOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const values = applied[0]?.values ?? form
  const fixed = pickStringArrayByKey(values, /fixed/i)
  const ignore = pickStringArrayByKey(values, /ignore/i)
  const transpose = pickStringArrayByKey(values, /transpose(?!.*header)/i)
  const transposeHeaders = toTransposeHeaders(values)
  if (fixed.length === 0 && ignore.length === 0 && transpose.length === 0 && transposeHeaders.length === 0) {
    return null
  }
  const payload: TransposeOperationObject = {
    SourceTable: sourceTable,
    fixed,
    ignore,
    transpose,
    transposeHeaders,
  }
  return {
    TP01: JSON.stringify(payload),
  }
}
function toBoxcoxRow(values: Record<string, unknown>, sourceTable: string): BoxcoxOperationRow | null {
  const columnName = pickStringField(values, /column.*name|column|field/i)
  const newColumnName = pickStringField(values, /new.*column.*name|output.*column|new.*column/i)
  const lamdaValue = pickStringField(values, /lamda|lambda|value/i)
  if (!sourceTable || !columnName || !newColumnName || !lamdaValue) return null
  return {
    SourceTable: sourceTable,
    column_name: columnName,
    new_column_name: newColumnName,
    lamda_value: lamdaValue,
  }
}
function buildBoxcoxOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): BoxcoxOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toBoxcoxRow(item.values, sourceTable))
    .filter((row): row is BoxcoxOperationRow => row !== null)
  const fallbackRow = toBoxcoxRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    BC01: JSON.stringify(finalRows),
  }
}
function toReplaceNullRow(values: Record<string, unknown>, sourceTable: string): ReplaceNullOperationRow | null {
  const columnName = String(values.column_name ?? '').trim()
  const mode = String(values.mode ?? '').trim()
  
  if (!sourceTable || !columnName || !mode) return null
  
  let replacenullwith = ''
  let method = null
  
  if (mode === 'value') {
    // Use the "Replace With Value" field
    replacenullwith = String(values.replacenullwith ?? '').trim()
  } else if (mode === 'method') {
    // Use the "Method" field
    const methodValue = String(values.method ?? '').trim()
    replacenullwith = methodValue
    method = methodValue
  }
  
  if (!replacenullwith) return null
  
  return {
    TransformationNo: 'RN01',
    SourceTable: sourceTable,
    column_name: columnName,
    replacenullwith: replacenullwith,
    method: method,
  }
}
function buildReplaceNullOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): ReplaceNullOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toReplaceNullRow(item.values, sourceTable))
    .filter((row): row is ReplaceNullOperationRow => row !== null)
  const fallbackRow = toReplaceNullRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    RN01: JSON.stringify(finalRows),
  }
}
function toDateToIntegerRow(
  values: Record<string, unknown>,
  sourceTable: string
): DateToIntegerOperationRow | null {
  const dateColumn = pickStringField(values, /date.*column|source.*date|column/i)
  const newDateKeyColumnName = pickStringField(values, /new.*date.*key.*column|new.*column|output.*column/i)
  const keyType = pickStringField(values, /key.*type|data.*type|type/i) || 'int'
  if (!sourceTable || !dateColumn || !newDateKeyColumnName) return null
  return {
    TransformationNo: 'DI01',
    SourceTable: sourceTable,
    DateColumn: dateColumn,
    NewDateKeyColumnName: newDateKeyColumnName,
    KeyType: keyType,
  }
}
function buildDateToIntegerOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): DateToIntegerOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toDateToIntegerRow(item.values, sourceTable))
    .filter((row): row is DateToIntegerOperationRow => row !== null)
  const fallbackRow = toDateToIntegerRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    DI01: JSON.stringify(finalRows),
  }
}
function toZscoreRow(values: Record<string, unknown>, sourceTable: string): ZscoreOperationRow | null {
  const zscoreOnColumn = pickStringField(values, /zscore.*column|z.*score.*on|column|field/i)
  const newColumn = pickStringField(values, /new.*column|output.*column|result.*column/i)
  if (!sourceTable || !zscoreOnColumn || !newColumn) return null
  return {
    SourceTable: sourceTable,
    zscore_on_column: zscoreOnColumn,
    new_column: newColumn,
  }
}
function buildZscoreOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): ZscoreOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toZscoreRow(item.values, sourceTable))
    .filter((row): row is ZscoreOperationRow => row !== null)
  const fallbackRow = toZscoreRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    ZS01: JSON.stringify(finalRows),
  }
}
function toSplitRow(values: Record<string, unknown>, sourceTable: string): SplitOperationRow | null {
  const column = pickStringField(values, /column|field/i)
  
  // Handle separator with special logic for empty values and spaces
  let separator = ''
  const separatorValue = Object.entries(values).find(([key]) => 
    /separator|delimiter|split.*by/i.test(key)
  )?.[1]
  
  if (separatorValue !== undefined && separatorValue !== null) {
    separator = String(separatorValue)
    // If separator is explicitly empty string, keep it empty
    // If separator is just spaces, keep the spaces
    // Don't default to ' ' anymore - use exactly what user provided
  } else {
    // Only default to space if the field is completely missing
    separator = ' '
  }
  
  const newColumnsRaw =
    Object.entries(values).find(([key]) => /new.*columns|output.*columns|columns/i.test(key))?.[1] ?? ''
  const newColumns = Array.isArray(newColumnsRaw)
    ? newColumnsRaw.map(v => String(v ?? '').trim()).filter(Boolean).join(',')
    : String(newColumnsRaw ?? '').trim()
  if (!sourceTable || !column || !newColumns) return null
  return {
    TransformationNo: 'SP01',
    SourceTable: sourceTable,
    Column: column,
    Separator: separator,
    NewColumns: newColumns,
  }
}
function buildSplitOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): SplitOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toSplitRow(item.values, sourceTable))
    .filter((row): row is SplitOperationRow => row !== null)
  const fallbackRow = toSplitRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    SP01: JSON.stringify(finalRows),
  }
}
function toForwardFillRow(
  values: Record<string, unknown>,
  sourceTable: string
): ForwardFillOperationRow | null {
  const forwardFillOnColumn = pickStringField(values, /forward.*fill.*column|fill.*column|column|field/i)
  const groupBy = pickStringField(values, /group.*by|partition.*by/i)
  const orderBy = pickStringField(values, /order.*by|sort.*by/i)
  if (!sourceTable || !forwardFillOnColumn) return null
  return {
    TransformationNo: 'FF01',
    SourceTable: sourceTable,
    ForwardFillOnColumn: forwardFillOnColumn,
    GroupBy: groupBy,
    OrderBy: orderBy,
  }
}
function buildForwardFillOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): ForwardFillOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toForwardFillRow(item.values, sourceTable))
    .filter((row): row is ForwardFillOperationRow => row !== null)
  const fallbackRow = toForwardFillRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    FF01: JSON.stringify(finalRows),
  }
}
function buildDateSplitOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): DateSplitOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const values = applied[0]?.values ?? form
  
  // Use the exact field name from API schema
  const dateColumn = String(values.DateColumn ?? '').trim() || 
                     pickStringField(values, /date.*column|DateColumn/i)
  
  if (!dateColumn) return null
  return {
    DS01: JSON.stringify({
      SourceTable: sourceTable,
      DateColumn: dateColumn,
    }),
  }
}
function toReplaceMappingList(values: Record<string, unknown>): [string, string, string, string][] {
  const raw = values.replaceMappingList
  if (!Array.isArray(raw)) return []
  const rows: [string, string, string, string][] = []
  raw.forEach(item => {
    if (Array.isArray(item) && item.length >= 4) {
      const a = String(item[0] ?? '').trim()
      const b = String(item[1] ?? '').trim()
      const c = String(item[2] ?? '').trim()
      const d = String(item[3] ?? '').trim()
      if (a && b && c && d) rows.push([a, b, c, d])
      return
    }
    if (item && typeof item === 'object') {
      const row = item as Record<string, unknown>
      const mapColumn = String(row.mapColumn ?? '').trim()
      const mapValue = String(row.mapValue ?? row.mapValueInColumn ?? '').trim()
      const replaceColumn = String(row.replaceColumn ?? row.replaceInColumn ?? '').trim()
      const replaceValue = String(row.replaceValue ?? row.replaceWithValue ?? '').trim()
      if (mapColumn && mapValue && replaceColumn && replaceValue) {
        rows.push([mapColumn, mapValue, replaceColumn, replaceValue])
      }
    }
  })
  return rows
}

function createReplaceMappingRule(): ReplaceMappingRule {
  return {
    mapColumn: '',
    mapValue: '',
    replaceColumn: '',
    replaceValue: ''
  }
}

// ─── Replace Null Types and Helper ─────────────────────────────────────
type ReplaceNullMode = 'value' | 'method'

const REPLACE_NULL_METHODS = [
  { value: 'mean', label: 'Mean' },
  { value: 'min', label: 'Min' },
  { value: 'max', label: 'Max' },
  { value: 'sum', label: 'Sum' },
  { value: 'stddev', label: 'Std Dev' }
]
function buildReplaceMappingOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): ReplaceMappingOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const appliedRows = applied.flatMap(item => toReplaceMappingList(item.values))
  const replaceMappingList = appliedRows.length > 0 ? appliedRows : toReplaceMappingList(form)
  if (replaceMappingList.length === 0) return null
  return {
    RM01: JSON.stringify({
      SourceTable: sourceTable,
      replaceMappingList,
    }),
  }
}
function toAggregationRows(
  values: Record<string, unknown>
): [string, string[] | null, string, string, null][] {
  const raw = values.aggregateList
  if (!Array.isArray(raw)) return []
  const rows: [string, string[] | null, string, string, null][] = []
  raw.forEach(item => {
    if (!item || typeof item !== 'object') return
    const row = item as Record<string, unknown>
    const aggregateType = String(row.aggregateType ?? row.aggregate_type ?? '').trim()
    const aggregateColumn = String(row.aggregateColumn ?? row.aggregate_column ?? '').trim()
    const newColumnName = String(row.newColumnName ?? row.new_column_name ?? '').trim()
    const groupColumnRaw = String(row.groupColumn ?? row.group_column ?? '').trim()
    const grouped = groupColumnRaw ? [groupColumnRaw] : null
    if (!aggregateType || !aggregateColumn || !newColumnName) return
    rows.push([aggregateType, grouped, aggregateColumn, newColumnName, null])
  })
  return rows
}
function buildAggregationOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): AggregationOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const appliedRows = applied.flatMap(item => toAggregationRows(item.values))
  const aggregateList = appliedRows.length > 0 ? appliedRows : toAggregationRows(form)
  if (aggregateList.length === 0) return null
  return {
    AG01: {
      SourceTable: sourceTable,
      aggregateList,
    },
  }
}
function toCsvString(value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(v => String(v ?? '').trim()).filter(Boolean).join(',')
  }
  return String(value ?? '').trim()
}
function toLookupRow(values: Record<string, unknown>, sourceTable: string): LookupOperationRow | null {
  const sourceColumn = String(values.SourceColumn ?? values.sourceColumn ?? '').trim()
  const lookupTableName = String(values.LookupTableName ?? values.lookupTableName ?? '').trim()
  const lookupTableDataSource = String(
    values.LookupTable_DataSource ?? values.lookupTableDataSource ?? ''
  ).trim()
  const lookupTableIsMaster = String(
    values.LookupTableName_isMaster ?? values.lookupTableName_isMaster ?? 'No'
  ).trim()
  const lookUpColumns = toCsvString(values.LookUpColumns ?? values.lookUpColumns)
  const lookUpGetColumns = toCsvString(values.LookUpGetColumns ?? values.lookUpGetColumns)
  if (!sourceTable || !sourceColumn || !lookupTableName) return null
  return {
    TransformationNo: 'LK01',
    SourceTable: sourceTable,
    SourceColumn: sourceColumn,
    LookupTableName: lookupTableName,
    LookupTable_DataSource: lookupTableDataSource,
    LookupTableName_isMaster: lookupTableIsMaster || 'No',
    LookUpColumns: lookUpColumns,
    LookUpGetColumns: lookUpGetColumns,
  }
}
function buildLookupOperationPayload(
  applied: AppliedTransformation[],
  form: Record<string, unknown>
): LookupOperationPayload | null {
  const sourceTable =
    applied.map(item => pickSourceTable(item.values)).find(Boolean) || pickSourceTable(form)
  if (!sourceTable) return null
  const rows = applied
    .map(item => toLookupRow(item.values, sourceTable))
    .filter((row): row is LookupOperationRow => row !== null)
  const fallbackRow = toLookupRow(form, sourceTable)
  const finalRows = rows.length > 0 ? rows : fallbackRow ? [fallbackRow] : []
  if (finalRows.length === 0) return null
  return {
    LK01: JSON.stringify(finalRows),
  }
}

// ─── Dynamic Operation Builder ──────────────────────────────────────
function buildDynamicOperationPayload(
  transformType: TransformTypeDefinition,
  appliedTransformations: AppliedTransformation[],
  form: Record<string, unknown>
): Record<string, unknown> {
  const schema = transformType.schema_definition
  if (!schema?.apiKey || !schema?.version) return {}

  const operationKey = schema.version // e.g., "SR01" for sort
  const values = appliedTransformations.length > 0 ? appliedTransformations[0]?.values ?? form : form

  // For direct transform type, build the operation object directly
  if (schema.transform?.type === 'direct') {
    const operationData: Record<string, unknown> = {}

    // Map form values to operation structure
    for (const [key, value] of Object.entries(values)) {
      if (key.startsWith('__')) continue // Skip meta fields
      operationData[key] = value
    }

    return {
      [`${transformType.abbreviation.toLowerCase()}OperationJson`]: {
        [operationKey]: operationData
      }
    }
  }

  return {}
}

function buildLegacyOperationPayloads(
  connectorId: string,
  transformName: string | undefined,
  appliedTransformations: AppliedTransformation[],
  formToSave: Record<string, unknown>,
  transformId?: number
): Record<string, unknown> {
  const legacyPayloads: Record<string, unknown> = {}

  // Keep existing legacy builders for backward compatibility
  if (isRenameColumnsTransform(connectorId, transformName)) {
    const payload = buildRenameColumnsOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.renameColumnsOperationJson = payload
  }

  if (isFindReplaceTransform(connectorId, transformName)) {
    const payload = buildFindReplaceOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.findReplaceOperationJson = payload
  }

  if (isDataTypeConversionTransform(connectorId, transformName)) {
    const payload = buildDataTypeConversionOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.dataTypeConversionOperationJson = payload
  }

  if (isConcatTransform(connectorId, transformName)) {
    const payload = buildConcatOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.concatOperationJson = payload
  }

  if (isDropFieldsTransform(connectorId, transformName)) {
    const payload = buildDropFieldsOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.dropFieldsOperationJson = payload
  }

  if (isCaseSetTransform(connectorId, transformName)) {
    const payload = buildCaseSetOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.caseSetOperationJson = payload
  }

  if (isFilterTransform(connectorId, transformName)) {
    const payload = buildFilterOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.filterOperationJson = payload
  }

  if (isDropRowsTransform(connectorId, transformName)) {
    const payload = buildDropRowsOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.dropRowsOperationJson = payload
  }

  if (isDropDuplicatesTransform(connectorId, transformName)) {
    const payload = buildDropDuplicatesOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.dropDuplicatesOperationJson = payload
  }

  if (isCumulativeTransform(connectorId, transformName)) {
    const payload = buildCumulativeOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.cumulativeOperationJson = payload
  }

  if (isDerivedColumnTransform(connectorId, transformName)) {
    const payload = buildDerivedColumnOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.derivedColumnOperationJson = payload
  }

  if (isTransposeTransform(connectorId, transformName)) {
    const payload = buildTransposeOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.transposeOperationJson = payload
  }

  if (isBoxcoxTransform(connectorId, transformName)) {
    const payload = buildBoxcoxOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.boxcoxOperationJson = payload
  }

  if (isReplaceNullTransform(connectorId, transformName)) {
    const payload = buildReplaceNullOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.replaceNullOperationJson = payload
  }

  if (isDateToIntegerTransform(connectorId, transformName)) {
    const payload = buildDateToIntegerOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.dateToIntegerOperationJson = payload
  }

  if (isZscoreTransform(connectorId, transformName)) {
    const payload = buildZscoreOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.zscoreOperationJson = payload
  }

  if (isSplitTransform(connectorId, transformName, transformId)) {
    const payload = buildSplitOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.splitOperationJson = payload
  }

  if (isForwardFillTransform(connectorId, transformName)) {
    const payload = buildForwardFillOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.forwardFillOperationJson = payload
  }

  if (isDateSplitTransform(connectorId, transformName, transformId)) {
    const payload = buildDateSplitOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.dateSplitOperationJson = payload
  }

  if (isReplaceMappingTransform(connectorId, transformName)) {
    const payload = buildReplaceMappingOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.replaceMappingOpeartionJson = payload
  }

  if (isAggregationTransform(connectorId, transformName)) {
    const payload = buildAggregationOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.aggregationOpeartionJson = payload
  }

  if (isLookupTransform(connectorId, transformName)) {
    const payload = buildLookupOperationPayload(appliedTransformations, formToSave)
    if (payload) legacyPayloads.lookupOpeartionJson = payload
  }

  // Skip sort for legacy since we handle it dynamically now
  return legacyPayloads
}


// ─── ReplaceMappingRulesRenderer ─────────────────────────────────────
type ReplaceMappingRule = {
  mapColumn: string
  mapValue: string
  replaceColumn: string
  replaceValue: string
}

type ReplaceMappingRulesRendererProps = {
  value: ReplaceMappingRule[]
  columnOptions: DynamicFieldOption[]
  onChange: (rules: ReplaceMappingRule[]) => void
  isReadonly?: boolean
}

function ReplaceMappingRulesRenderer({ 
  value, 
  columnOptions, 
  onChange, 
  isReadonly = false 
}: ReplaceMappingRulesRendererProps) {
  const rules = Array.isArray(value) ? value : []

  const addRule = useCallback(() => {
    onChange([...rules, createReplaceMappingRule()])
  }, [rules, onChange])

  const updateRule = useCallback((index: number, field: keyof ReplaceMappingRule, newValue: string) => {
    const newRules = [...rules]
    newRules[index] = { ...newRules[index], [field]: newValue }
    onChange(newRules)
  }, [rules, onChange])

  const removeRule = useCallback((index: number) => {
    const newRules = rules.filter((_, i) => i !== index)
    onChange(newRules)
  }, [rules, onChange])

  return (
    <div className={styles.field}>
      <Label className={styles.fieldLabel}>Mapping Rules</Label>
      <div className={styles.replaceMappingContainer}>
        {rules.length === 0 && (
          <p className={styles.noRulesText}>No mapping rules added yet. Click "Add Mapping Rule" to get started.</p>
        )}
        {rules.map((rule, index) => (
          <div key={index} className={styles.replaceMappingRule}>
            <div className={styles.replaceMappingFields}>
              <div className={styles.replaceMappingField}>
                <Label htmlFor={`mapColumn-${index}`} className={styles.smallLabel}>Map Column *</Label>
                <Select
                  value={rule.mapColumn}
                  onValueChange={(val) => updateRule(index, 'mapColumn', val)}
                  disabled={isReadonly}
                >
                  <SelectTrigger id={`mapColumn-${index}`} className={styles.smallSelect}>
                    <SelectValue placeholder="Select column" />
                  </SelectTrigger>
                  <SelectContent>
                    {columnOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className={styles.replaceMappingField}>
                <Label htmlFor={`mapValue-${index}`} className={styles.smallLabel}>Map Value *</Label>
                <Input
                  id={`mapValue-${index}`}
                  type="text"
                  value={rule.mapValue}
                  onChange={(e) => updateRule(index, 'mapValue', e.target.value)}
                  placeholder="Shows"
                  className={styles.smallInput}
                  readOnly={isReadonly}
                  disabled={isReadonly}
                />
              </div>
              
              <div className={styles.replaceMappingField}>
                <Label htmlFor={`replaceColumn-${index}`} className={styles.smallLabel}>Replace to Column *</Label>
                <Select
                  value={rule.replaceColumn}
                  onValueChange={(val) => updateRule(index, 'replaceColumn', val)}
                  disabled={isReadonly}
                >
                  <SelectTrigger id={`replaceColumn-${index}`} className={styles.smallSelect}>
                    <SelectValue placeholder="catdesc (string)" />
                  </SelectTrigger>
                  <SelectContent>
                    {columnOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className={styles.replaceMappingField}>
                <Label htmlFor={`replaceValue-${index}`} className={styles.smallLabel}>Replace With Value *</Label>
                <Input
                  id={`replaceValue-${index}`}
                  type="text"
                  value={rule.replaceValue}
                  onChange={(e) => updateRule(index, 'replaceValue', e.target.value)}
                  placeholder="Great Shows"
                  className={styles.smallInput}
                  readOnly={isReadonly}
                  disabled={isReadonly}
                />
              </div>
            </div>
            
            {!isReadonly && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className={styles.removeRuleButton}
                onClick={() => removeRule(index)}
                aria-label="Remove mapping rule"
              >
                <MaterialIcon name="delete" size={16} />
              </Button>
            )}
          </div>
        ))}
        
        {!isReadonly && (
          <Button
            type="button"
            variant="outline"
            className={styles.addMappingButton}
            onClick={addRule}
          >
            <MaterialIcon name="add" size={16} />
            Add Mapping Rule
          </Button>
        )}
      </div>
    </div>
  )
}

// ─── Field Visibility Helper ─────────────────────────────────────────
function isFieldVisible(field: DynamicField, form: Record<string, unknown>): boolean {
  if (!field.visibleIf) return true
  
  const { field: dependentField, equals } = field.visibleIf as { field: string; equals: string }
  const dependentValue = String(form[dependentField] ?? '')
  return dependentValue === equals
}

function isFieldRequired(field: DynamicField, form: Record<string, unknown>): boolean {
  if (field.required) return true
  if (!field.requiredIf) return false
  
  const { field: dependentField, equals } = field.requiredIf as { field: string; equals: string }
  const dependentValue = String(form[dependentField] ?? '')
  return dependentValue === equals
}

// ─── FieldRenderer ──────────────────────────────────────────────── 
type FieldRendererProps = {
  field: DynamicField
  value: unknown
  error?: string
  onChange: (key: string, value: unknown) => void
  isReadonly?: boolean
}
function FieldRenderer({ field, value, error, onChange, isReadonly = false }: FieldRendererProps) {
  const fieldId = `field-${field.key}`
  const isRequired = !!field.required
  const labelEl = (
    <Label className={styles.fieldLabel} htmlFor={fieldId}>
      {field.label} {isRequired && '*'}
    </Label>
  )
  // ── Radio buttons ────────────────────────────────────────────── 
  if (field.type === 'radio') {
    const options = field.options ?? []
    return (
      <div className={styles.field}>
        {labelEl}
        <div className={styles.radioGroup}>
          {options.map(opt => (
            <label key={opt.value} className={styles.radioLabel}>
              <input
                type="radio"
                name={`radio-${field.key}`}
                value={opt.value}
                checked={String(value ?? '') === opt.value}
                onChange={(e) => onChange(field.key, e.target.value)}
                disabled={isReadonly}
              />
              {opt.label}
            </label>
          ))}
        </div>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }
  // ── Single select ────────────────────────────────────────────── 
  if (field.type === 'select') {
    const options = field.options?.length
      ? field.options
      : FALLBACK_SCHEMA_OPTIONS.map(o => ({ value: o, label: o }))
    return (
      <div className={styles.field}>
        {labelEl}
        <Select
          value={String(value ?? '')}
          onValueChange={val => onChange(field.key, val)}
          disabled={isReadonly}
        >
          <SelectTrigger className={styles.selectTrigger}>
            <SelectValue placeholder="Select a Schema" />
          </SelectTrigger>
          <SelectContent>
            {!field.required && (
              <SelectItem value="_placeholder">Select</SelectItem>
            )}
            {options.map(opt => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }
  // ── Multiselect with shadcn/ui dropdown ────────────────────────
  if (field.type === 'multiselect') {
    const options = field.options?.length
      ? field.options
      : FALLBACK_SCHEMA_OPTIONS.map(o => ({ value: o, label: o }))

    const selectedValues = Array.isArray(value) ? value : []
    const selectedLabels = selectedValues.map(val =>
      options.find(opt => opt.value === val)?.label || val
    ).filter(Boolean)

    return (
      <div className={styles.field}>
        {labelEl}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className={`${styles.selectTrigger} justify-between text-left h-auto min-h-[36px] py-2`}
              type="button"
              disabled={isReadonly}
            >
              <div className="flex flex-wrap gap-1 overflow-hidden flex-1 min-w-0">
                {selectedLabels.length > 0 ? (
                  selectedLabels.length <= 2 ? (
                    selectedLabels.map((label, i) => (
                      <Badge key={i} variant="secondary" className="text-xs max-w-[120px] truncate">
                        {label}
                      </Badge>
                    ))
                  ) : (
                    <>
                      <Badge variant="secondary" className="text-xs max-w-[120px] truncate">
                        {selectedLabels[0]}
                      </Badge>
                      <Badge variant="outline" className="text-xs shrink-0">
                        +{selectedLabels.length - 1} more
                      </Badge>
                    </>
                  )
                ) : (
                  <span className="text-muted-foreground">Select columns...</span>
                )}
              </div>
              <MaterialIcon name="expand_more" size={16} className="ml-2 shrink-0" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] max-h-[300px] overflow-y-auto">
            {options.map(opt => (
              <DropdownMenuCheckboxItem
                key={opt.value}
                checked={selectedValues.includes(opt.value)}
                onCheckedChange={(checked) => {
                  const newValues = checked
                    ? [...selectedValues, opt.value]
                    : selectedValues.filter(val => val !== opt.value)
                  onChange(field.key, newValues)
                }}
              >
                {opt.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }
  // ── Textarea ─────────────────────────────────────────────────── 
  if (field.type === 'textarea') {
    return (
      <div className={styles.field}>
        {labelEl}
        <Textarea
          id={fieldId}
          className={styles.fieldTextarea}
          value={String(value ?? '')}
          placeholder={field.placeholder ?? ''}
          onChange={e => onChange(field.key, e.target.value)}
          readOnly={isReadonly}
          disabled={isReadonly}
        />
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }
  // ── Group ────────────────────────────────────────────────────── 
  if (field.type === 'group') {
    const groupField = field as DynamicField & {
      multiple?: boolean
      addButtonLabel?: string
      allowRemove?: boolean
    }
    const isRepeatable = !!(groupField.repeatable || groupField.multiple)
    const subFields = field.fields ?? []
    const addButtonLabel = groupField.addButtonLabel || `Add ${field.label}`
    const allowRemove = groupField.allowRemove !== false
    if (!isRepeatable) {
      const groupObj =
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)
          : (buildInitialFormState(subFields) as Record<string, unknown>)
      return (
        <div className={styles.field}>
          <Label className={styles.fieldLabel}>{field.label}</Label>
          <div className={styles.groupFields}>
            {subFields.map(subField => (
              <FieldRenderer
                key={`${field.key}-${subField.key}`}
                field={subField}
                value={groupObj[subField.key] ?? (subField.type === 'multiselect' ? [] : '')}
                error={error}
                onChange={(subKey, subValue) => {
                  onChange(field.key, { ...groupObj, [subKey]: subValue })
                }}
                isReadonly={isReadonly}
              />
            ))}
          </div>
          {error && <span className={styles.errorText}>{error}</span>}
        </div>
      )
    }
    const groupValue = Array.isArray(value) ? value : []
    return (
      <div className={styles.field}>
        <Label className={styles.fieldLabel}>{field.label}</Label>
        <div className={styles.groupContainer}>
          {groupValue.map((item, index) => {
            const row =
              item && typeof item === 'object' && !Array.isArray(item)
                ? (item as Record<string, unknown>)
                : {}
            return (
              <div key={`${field.key}-${index}`} className={styles.groupItem}>
                <div className={styles.groupFields}>
                  {subFields.map(subField => (
                    <FieldRenderer
                      key={`${field.key}-${index}-${subField.key}`}
                      field={subField}
                      value={row[subField.key] ?? (subField.type === 'multiselect' ? [] : '')}
                      error={error}
                      onChange={(subKey, subValue) => {
                        const next = [...groupValue]
                        next[index] = { ...row, [subKey]: subValue }
                        onChange(field.key, next)
                      }}
                      isReadonly={isReadonly}
                    />
                  ))}
                </div>
                {allowRemove && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className={styles.removeButton}
                    onClick={() => {
                      const next = groupValue.filter((_, i) => i !== index)
                      onChange(field.key, next)
                    }}
                  >
                    Remove
                  </Button>
                )}
              </div>
            )
          })}
          <Button
            type="button"
            variant="outline"
            className={styles.addGroupButton}
            onClick={() => {
              const next = [...groupValue, buildInitialFormState(subFields)]
              onChange(field.key, next)
            }}
          >
            {addButtonLabel}
          </Button>
        </div>
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    )
  }
  // ── Text input (default) ─────────────────────────────────────── 
  
  // Special handling for separator fields (only for regular split, not date split)
  const isSeparatorField = /separator|delimiter|split.*by/i.test(field.key)
  const separatorPlaceholder = isSeparatorField 
    ? 'Enter separator (leave empty for no separator, space for space separator)' 
    : field.placeholder ?? ''
  
  return (
    <div className={styles.field}>
      {labelEl}
      <Input
        id={fieldId}
        type="text"
        className={styles.fieldInput}
        value={String(value ?? '')}
        placeholder={separatorPlaceholder}
        onChange={e => onChange(field.key, e.target.value)}
        readOnly={isReadonly}
        disabled={isReadonly}
      />
      {isSeparatorField && (
        <div className={styles.fieldHint}>
          Empty = no separator, Space = split by space, Custom = split by your text
        </div>
      )}
      {error && <span className={styles.errorText}>{error}</span>}
    </div>
  )
}
// ─── Main component ─────────────────────────────────────────────── 
export function ConfigureTransformationSheet({
  onClose,
  onBackToChoose,
  transformationTitle,
  connectorImage,
  configuringNodeId,
  connectorId = 'findreplace',
}: ConfigureTransformationSheetProps) {
  const { selectedTransformType, fields, isLoading, error: apiError } =
    useTransformations(connectorId)
  const sessionSnapshot = useAdpPipelineSessionSnapshot()
  const [form, setForm] = useState<FormState>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [appliedTransformations, setAppliedTransformations] = useState<AppliedTransformation[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const selectedTableValue = useMemo(() => {
    const tableField = fields.find(f => integrationFieldLooksLikeTable(f))
    if (!tableField) return ''
    return String(form[tableField.key] ?? '').trim()
  }, [fields, form])
  const fieldsWithOptions = useMemo(() => {
    const tables = getSessionTableOptions()
    const cols = selectedTableValue
      ? getSessionColumnOptionsForTableSelection(selectedTableValue, '', [])
      : getExtendedColumnOptionsForPipeline()
    // Apply data type options first (for legacy fields), then session options (which preserves API-defined options)
    const fieldsWithDataTypes = applyDataTypeOptionsToFields(fields)
    return applySessionOptionsToFields(fieldsWithDataTypes, tables, cols)
  }, [fields, selectedTableValue, sessionSnapshot])
  useEffect(() => {
    const initial = buildInitialFormState(fields)

    // Auto-populate SourceTable field with the first available table
    const tables = getSessionTableOptions()
    const sourceTableField = fields.find(f => f.dataSource === 'tables_list' || f.key === 'SourceTable')
    if (sourceTableField && tables.length > 0 && !initial[sourceTableField.key]) {
      initial[sourceTableField.key] = tables[0].value
    }

    // For date to integer transformation, set KeyType to 'int' by default
    if (isDateToIntegerTransform(connectorId, selectedTransformType?.name)) {
      const keyTypeField = fields.find(f => /key.*type|keytype/i.test(f.key))
      if (keyTypeField) {
        initial[keyTypeField.key] = 'int'
      }
    }


    // For replace mapping transformation, initialize replaceMappingList as empty array
    if (isReplaceMappingTransform(connectorId, selectedTransformType?.name)) {
      initial.replaceMappingList = []
    }

    const saved =
      configuringNodeId != null
        ? readAdpPipelineSession().transformations[configuringNodeId]
        : undefined
    if (saved?.connectorId === connectorId && saved.form && Object.keys(saved.form).length > 0) {
      setForm(mergeSavedForm(initial, saved.form) as FormState)
      const restoredApplied = Array.isArray((saved.form as Record<string, unknown>).__appliedTransformations)
        ? ((saved.form as Record<string, unknown>).__appliedTransformations as SavedAppliedTransformation[])
          .map(item => ({
            id: String(item?.id ?? Date.now()),
            label: String(item?.label ?? 'Transformation'),
            values:
              item?.values && typeof item.values === 'object'
                ? (item.values as Record<string, unknown>)
                : {},
          }))
        : []
      setAppliedTransformations(restoredApplied)
    } else {
      setForm(initial)
      setAppliedTransformations([])
    }
    setErrors({})
  }, [fields, configuringNodeId, connectorId])
  const displayTitle =
    transformationTitle ?? selectedTransformType?.name ?? 'Transformation'
  const connectorDescription =
    selectedTransformType?.description ?? 'Configure transformation settings'
  const handleChange = useCallback(
    (key: string, value: unknown) => {
      setForm(prev => ({ ...prev, [key]: value }))
      if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }))
    },
    [errors]
  )
  const validate = useCallback((): boolean => {
    const next: Record<string, string> = {}
    
    // Special validation for replace mapping
    if (isReplaceMappingTransform(connectorId, selectedTransformType?.name)) {
      const rules = form.replaceMappingList as ReplaceMappingRule[] ?? []
      if (rules.length === 0) {
        next.replaceMappingList = 'At least one mapping rule is required'
      } else {
        for (let i = 0; i < rules.length; i++) {
          const rule = rules[i]
          if (!rule.mapColumn || !rule.mapValue || !rule.replaceColumn || !rule.replaceValue) {
            next.replaceMappingList = 'All fields in mapping rules are required'
            break
          }
        }
      }
    }
    
    fieldsWithOptions.forEach(field => {
      // Check if field is visible
      if (!isFieldVisible(field, form)) return
      
      // Check if field is required (either always or conditionally)
      const fieldRequired = isFieldRequired(field, form)
      if (!fieldRequired) return
      
      const value = form[field.key]
      
      if (field.type === 'multiselect') {
        if (!Array.isArray(value) || value.length === 0)
          next[field.key] = `${field.label} is required`
      } else {
        // Special handling for separator fields in regular split transformation (not date split)
        if (isSplitTransform(connectorId, selectedTransformType?.name, selectedTransformType?.id) && 
            !isDateSplitTransform(connectorId, selectedTransformType?.name, selectedTransformType?.id) &&
            /separator|delimiter|split.*by/i.test(field.key)) {
          // For separator fields, we accept any value including empty strings and spaces
          // Only fail validation if the value is undefined or null
          if (value === undefined || value === null) {
            next[field.key] = `${field.label} is required`
          }
        } else {
          // Standard validation for non-separator fields
          if (!value || String(value).trim() === '') {
            next[field.key] = `${field.label} is required`
          }
        }
      }
    })
    setErrors(next)
    return Object.keys(next).length === 0
  }, [fieldsWithOptions, form, connectorId, selectedTransformType?.name])
  const clearForm = useCallback(() => {
    setForm(buildInitialFormState(fields))
    setErrors({})
  }, [fields])
  const handleAdd = useCallback(() => {
    if (!validate()) return
    const values = toPersistableForm(form)
    
    // Create user-friendly labels based on transformation type
    let label = `${displayTitle} ${appliedTransformations.length + 1}`
    
    if (isReplaceNullTransform(connectorId, selectedTransformType?.name)) {
      const columnName = String(form.column_name ?? '')
      const mode = String(form.mode ?? '')
      if (columnName && mode) {
        const strategy = mode === 'value' 
          ? `with "${form.replacenullwith ?? ''}"` 
          : `using ${form.method ?? ''}`
        label = `${displayTitle} ${columnName} ${strategy}`
      }
    } else if (isDateSplitTransform(connectorId, selectedTransformType?.name, selectedTransformType?.id)) {
      const dateColumn = String(form.DateColumn ?? '')
      if (dateColumn) {
        label = `${displayTitle} on ${dateColumn}`
      }
    } else if (isReplaceMappingTransform(connectorId, selectedTransformType?.name)) {
      const rules = form.replaceMappingList as ReplaceMappingRule[] ?? []
      label = `${displayTitle} (${rules.length} rule${rules.length === 1 ? '' : 's'})`
    }
    
    const newItem: AppliedTransformation = {
      id: Date.now().toString(),
      label,
      values,
    }
    setAppliedTransformations(prev => [...prev, newItem])
    clearForm()
  }, [validate, form, displayTitle, appliedTransformations.length, clearForm, connectorId, selectedTransformType?.name])
  const handleDeleteApplied = useCallback((id: string) => {
    setAppliedTransformations(prev => prev.filter(t => t.id !== id))
  }, [])
  const handleSave = useCallback(async () => {
    setIsSubmitting(true)
    try {
      if (configuringNodeId && selectedTransformType) {
        const formToSave = toPersistableForm(form)

        // Build dynamic operation JSON based on API schema
        const operationJson = buildDynamicOperationPayload(
          selectedTransformType,
          appliedTransformations,
          formToSave
        )

        // Legacy support for hardcoded transformations
        const legacyOperationJsons = buildLegacyOperationPayloads(
          connectorId,
          selectedTransformType?.name,
          appliedTransformations,
          formToSave,
          selectedTransformType?.id
        )

        upsertTransformationSession(configuringNodeId, connectorId, {
          ...formToSave,
          ...operationJson,
          ...legacyOperationJsons,
          __appliedTransformations: appliedTransformations.map(item => ({
            id: item.id,
            label: item.label,
            values: item.values,
          })),
        })
      }
      await new Promise(resolve => setTimeout(resolve, 800))
      onClose()
    } finally {
      setIsSubmitting(false)
    }
  }, [onClose, configuringNodeId, connectorId, form, appliedTransformations, selectedTransformType])
  return (
    <aside
      className={styles.root}
      aria-label={`Configure ${displayTitle}`}
      data-configuring-node={configuringNodeId ?? undefined}
    >
      <div className={styles.inner}>
        {/* ── Header ─────────────────────────────────────────────── */}
        <div className={styles.headerRow}>
          {onBackToChoose && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={styles.backChevron}
              onClick={onBackToChoose}
              aria-label="Back"
            >
              <MaterialIcon name="chevron_left" size={20} />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={styles.closeChevron}
            onClick={onClose}
            aria-label="Close panel"
          >
            <span aria-hidden>&gt;&gt;</span>
          </Button>
          <h2 className={styles.pageTitle}>Transformation Details</h2>
        </div>
        <p className={styles.pageSubtitle}>
          Enter the transformation details for the source
        </p>
        <Separator className={styles.divider} />
        {/* ── Connector row ───────────────────────────────────────── */}
        <div className={styles.connectorRow}>
          <div className={styles.iconWell}>
            {connectorImage ? (
              <img src={connectorImage} alt="" />
            ) : (
              <MaterialIcon name="transform" size={24} />
            )}
          </div>
          <div className={styles.connectorText}>
            <p className={styles.connectorName}>{displayTitle}</p>
            <p className={styles.connectorDesc}>{connectorDescription}</p>
          </div>
        </div>
        {/* ── Body ────────────────────────────────────────────────── */}
        <div className={styles.body}>
          {/* Left — form card */}
          <div className={styles.formCard}>
            {isLoading ? (
              <div className={styles.loading}>
                Loading transform configuration...
              </div>
            ) : apiError ? (
              <p className={styles.errorText}>Error: {apiError}</p>
            ) : isReplaceMappingTransform(connectorId, selectedTransformType?.name) ? (
              // Special handling for replace mapping transformation
              <>
                {fieldsWithOptions.filter(field => 
                  !['mapColumn', 'mapValue', 'replaceColumn', 'replaceValue', 'replaceMappingList'].includes(field.key)
                ).map(field => (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={form[field.key] ?? (field.type === 'multiselect' ? [] : '')}
                    error={errors[field.key]}
                    onChange={handleChange}
                  />
                ))}
                <div className={styles.field}>
                  <ReplaceMappingRulesRenderer
                    value={form.replaceMappingList as ReplaceMappingRule[] ?? []}
                    columnOptions={fieldsWithOptions.find(f => f.key === 'mapColumn')?.options ?? 
                      getExtendedColumnOptionsForPipeline().map(col => ({ value: col.value, label: col.label }))}
                    onChange={(rules) => handleChange('replaceMappingList', rules)}
                  />
                  {errors.replaceMappingList && (
                    <span className={styles.errorText}>{errors.replaceMappingList}</span>
                  )}
                </div>
              </>
            ) : fieldsWithOptions.length ? (
              fieldsWithOptions.map(field => {
                // Check if this field should be readonly (KeyType in date to integer transformation)
                const isKeyTypeInDateToInteger =
                  isDateToIntegerTransform(connectorId, selectedTransformType?.name) &&
                  /key.*type|keytype/i.test(field.key)

                // Check field visibility based on conditional logic
                const isVisible = isFieldVisible(field, form)
                if (!isVisible) return null

                return (
                  <FieldRenderer
                    key={field.key}
                    field={field}
                    value={form[field.key] ?? (field.type === 'multiselect' ? [] : '')}
                    error={errors[field.key]}
                    onChange={handleChange}
                    isReadonly={isKeyTypeInDateToInteger}
                  />
                )
              }).filter(Boolean)
            ) : (
              <p className={styles.noConfig}>
                No dynamic configuration fields found for this transform.
              </p>
            )}
            <div className={styles.formActions}>
              <Button
                type="button"
                variant="outline"
                className={styles.clearBtn}
                onClick={clearForm}
              >
                Clear
              </Button>
              <Button
                type="button"
                className={styles.addBtn}
                onClick={handleAdd}
              >
                Add
              </Button>
            </div>
          </div>
          {/* Right — applied transformations */}
          <div className={styles.appliedCard}>
            <p className={styles.appliedTitle}>Applied Transformations</p>
            <div className={styles.appliedList}>
              {appliedTransformations.map(t => (
                <div key={t.id} className={styles.appliedItem}>
                  <span>{t.label}</span>
                  <div className={styles.appliedItemActions}>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={styles.iconBtn}
                      aria-label={`Edit ${t.label}`}
                    >
                      <MaterialIcon name="edit" size={16} />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={`${styles.iconBtn} ${styles.danger}`}
                      aria-label={`Delete ${t.label}`}
                      onClick={() => handleDeleteApplied(t.id)}
                    >
                      <MaterialIcon name="delete" size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* ── Footer ──────────────────────────────────────────────── */}
        <div className={styles.actions}>
          <Button
            type="button"
            variant="outline"
            className={styles.cancelBtn}
            onClick={clearForm}
            disabled={isSubmitting}
          >
            Reset
          </Button>
          <Button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </aside>
  )
} 